import { Dispatch, SetStateAction, useCallback } from 'react';
import { useLocalStorage } from '@shared/util/storage/useLocalStorage';
import { allowLocalFeatureFlagOverrides } from './allowLocalFeatureFlagOverrides';
import { DEVELOPER_TOOLS, FlagName, Flags } from './flags';

type FeatureFlagValue<T extends FlagName> = Flags[T] | null;
type FeatureFlagSetter<T extends FlagName> = Dispatch<SetStateAction<FeatureFlagValue<T>>>;

export function useLocalFeatureFlag<T extends FlagName>(
  flagName: T,
): [FeatureFlagValue<T>, FeatureFlagSetter<T>, VoidFunction] {
  const key = `feature-flag:${flagName}`;
  const [storedFlag, setStoredFlag, , removeStoredFlag] = useLocalStorage<FeatureFlagValue<T>>(
    key,
    null,
  );

  // `developer-tools` unlocks the dev menu via version taps; not subject to prod LS override lockout.
  const overridesAllowed = allowLocalFeatureFlagOverrides || flagName === DEVELOPER_TOOLS;

  const effectiveFlag: FeatureFlagValue<T> = overridesAllowed ? storedFlag : null;

  const setLocalFlag: FeatureFlagSetter<T> = useCallback(
    (update: SetStateAction<FeatureFlagValue<T>>) => {
      if (!overridesAllowed) {
        return;
      }
      setStoredFlag(update);
    },
    [overridesAllowed, setStoredFlag],
  );

  const removeLocalFlag = useCallback(() => {
    if (!overridesAllowed) {
      return;
    }
    removeStoredFlag();
  }, [overridesAllowed, removeStoredFlag]);

  return [effectiveFlag, setLocalFlag, removeLocalFlag];
}
